import storehouseApi from '@/api/storehouse';
import { IngredientProps } from '@/interfaces/models/ingredient';
import { ProductProps } from '@/interfaces/models/product';
import Ingredient from '@/models/ingredient';
import Product from '@/models/product';
import StorehouseIngredient from '@/models/storehouseIngredient';
import StorehouseProduct from '@/models/storehouseProduct';
import StorehouseUnitInterface from '@/models/storehouseUnitInterface';
import Transaction, { TransactionType } from '@/models/transaction';
import TransactionIngredient from '@/models/transactionIngredient';
import TransactionProduct from '@/models/transactionProduct';
import { RootState } from '@/store';
import SystemDate from '@/utils/systemDate';
import { ActionTree, GetterTree, MutationTree, ActionContext } from 'vuex';

type StorehouseState = {
  isStorehouseFetching: boolean,
  isStorehouseWasFetch: boolean,
  storehouseUnits: StorehouseUnitInterface[],
  storehouses: any[],
  otherStorehouses: any[],
  transactions: any[],
  storehouseUnitSearchText: string,
  generalSortType: string,
  selectedStorehouseUnits: any[]
};

const namespaced: boolean = true;

const getDefaultState = (): StorehouseState => {
  return {
    isStorehouseFetching: false,
    isStorehouseWasFetch: false,
    storehouseUnits: [],
    storehouses: [],
    otherStorehouses: [],
    transactions: [],
    storehouseUnitSearchText: '',
    generalSortType: 'auto',
    selectedStorehouseUnits: [],
  };
};

const state: StorehouseState = getDefaultState();

const getters: GetterTree<StorehouseState, RootState> = {
  activeTransactions(state) {
    return state.transactions.filter(({ isComplete }: any) => !isComplete);
  },
  transactionsGroupedByMonth(state) {
    return state.transactions
      .reduce((groups: { [key: string]: Transaction[] }, transaction: Transaction) => {
        if (transaction.isComplete) {
          const hash = new SystemDate(transaction.createdAt * 1000).getSmallHash();

          if (groups.hasOwnProperty(hash)) {
            groups[hash].push(transaction);
          } else {
            groups[hash] = [transaction];
          }
        }

        return groups;
      }, {});
  },
  getTransactionsFilteredByTypeAndGroupedByMonth(state) {
    return (filterBy: TransactionType) => {
      return state.transactions
        .reduce((groups: { [key: string]: Transaction[] }, transaction: Transaction) => {
          if (filterBy === transaction.type && transaction.isComplete) {
            const hash = new SystemDate(transaction.createdAt * 1000).getSmallHash();

            if (groups.hasOwnProperty(hash)) {
              groups[hash].push(transaction);
            } else {
              groups[hash] = [transaction];
            }
          }

          return groups;
        }, {});
    };
  },
  getTransactionsByProductGroupedByMonth(state) {
    return (neededStorehouseProduct: StorehouseProduct) => {
      return state.transactions
        .reduce((groups: { [key: string]: Transaction[] }, transaction: Transaction) => {
          const isTransactionContainProduct = transaction.products.some(({ id }: any) => {
            return id === neededStorehouseProduct.id;
          });

          if (isTransactionContainProduct && transaction.isComplete) {
            const hash = new SystemDate(transaction.createdAt * 1000).getSmallHash();

            if (groups.hasOwnProperty(hash)) {
              groups[hash].push(transaction);
            } else {
              groups[hash] = [transaction];
            }
          }

          return groups;
        }, {});
    };
  },
  getTransactionsByIngredientGroupedByMonth(state) {
    return (neededStorehouseIngredient: StorehouseIngredient) => {
      return state.transactions
        .reduce((groups: { [key: string]: Transaction[] }, transaction: Transaction) => {
          const isTransactionContainIngredient = transaction.ingredients.some(({ id }: any) => {
            return id === neededStorehouseIngredient.id;
          });

          if (isTransactionContainIngredient && transaction.isComplete) {
            const hash = new SystemDate(transaction.createdAt * 1000).getSmallHash();

            if (groups.hasOwnProperty(hash)) {
              groups[hash].push(transaction);
            } else {
              groups[hash] = [transaction];
            }
          }

          return groups;
        }, {});
    };
  },
  getSelectedStorehouseUnitFinder(state) {
    return (neededStorehouseUnit: StorehouseUnitInterface): StorehouseUnitInterface | undefined => {
      return state.selectedStorehouseUnits.find((storehouseUnit: StorehouseUnitInterface) => {
        return neededStorehouseUnit.id === storehouseUnit.id
          && (
            (neededStorehouseUnit instanceof StorehouseProduct && storehouseUnit instanceof StorehouseProduct)
            || (neededStorehouseUnit instanceof StorehouseIngredient && storehouseUnit instanceof StorehouseIngredient)
          );
      });
    };
  },
  getIsSelectedStorehouseUnit(state) {
    return (id: string): boolean => {
      return state.selectedStorehouseUnits.some(
        (storehouseUnit: StorehouseIngredient) => storehouseUnit.id === id,
      );
    };
  },
  getStorehouseUnitByProduct(state) {
    return (product: TransactionProduct): StorehouseUnitInterface | undefined => {
      const neededProduct = state.storehouseUnits.find(
        (storehouseUnit: StorehouseUnitInterface) => storehouseUnit.id === product.id,
      );

      if (undefined !== neededProduct) {
        return neededProduct;
      }

      for (const storehouse of state.otherStorehouses) {
        const neededProduct = storehouse.products.find(
          (storehouseUnit: StorehouseUnitInterface) => storehouseUnit.id === product.id,
        );

        if (undefined !== neededProduct) {
          return neededProduct;
        }
      }
    };
  },
  getStorehouseUnitByIngredient(state) {
    return (ingredient: TransactionIngredient): StorehouseUnitInterface | undefined => {
      const neededIngredient = state.storehouseUnits.find(
        (storehouseUnit: StorehouseUnitInterface) => storehouseUnit.id === ingredient.id,
      );

      if (undefined !== neededIngredient) {
        return neededIngredient;
      }

      for (const storehouse of state.otherStorehouses) {
        const neededIngredient = storehouse.ingredients.find(
          (storehouseUnit: StorehouseUnitInterface) => storehouseUnit.id === ingredient.id,
        );

        if (undefined !== neededIngredient) {
          return neededIngredient;
        }
      }
    };
  },
};

const actions: ActionTree<StorehouseState, RootState> = {
  async fetchStorehouse({ commit }: ActionContext<StorehouseState, RootState>) {
    if (!state.isStorehouseWasFetch) {
      commit('setIsStorehouseFetching', true);

      return storehouseApi.fetchStorehouse()
        .then((response: any) => {
          commit('setStorehouseUnits', [...response.products, ...response.ingredients]);
          commit('setStorehouses', response.storehouses);
          commit('setOtherStorehouses', response.otherStorehouses);
          commit('setTransactions', response.transactions);
          commit('setIsStorehouseWasFetch', true);
          commit('setIsStorehouseFetching', false);
        });
    }
  },
  async completeTransaction({ dispatch }: ActionContext<StorehouseState, RootState>, transaction: Transaction) {
    if (transaction.isShared) {
      storehouseApi.completeSharedTransaction(transaction);
    } else {
      storehouseApi.completeRequestTransaction(transaction);
    }

    dispatch('socketCompleteStorehouseTransaction', { id: transaction.id }, { root: true });
  },
  async cancelTransaction({ dispatch }: ActionContext<StorehouseState, RootState>, transaction: Transaction) {
    storehouseApi.cancelTransaction(transaction);

    dispatch('socketCancelStorehouseTransaction', { id: transaction.id }, { root: true });
  },
  async getContractorsByContractorName({ dispatch }: ActionContext<StorehouseState, RootState>, name: string) {
    return storehouseApi.fetchContractorsByName(name);
  },
  async createTransaction({ commit, dispatch }: ActionContext<StorehouseState, RootState>, transaction: any) {
    const products = transaction.storehouseUnits.reduce(
      (products: any, storehouseUnit: StorehouseUnitInterface) => {
        if (storehouseUnit instanceof StorehouseProduct) {
          products[storehouseUnit.id] = storehouseUnit.count;
        }

        return products;
      },
      {},
    );
    const ingredients = transaction.storehouseUnits.reduce(
      (ingredients: any, storehouseUnit: StorehouseUnitInterface) => {
        if (storehouseUnit instanceof StorehouseIngredient) {
          ingredients[storehouseUnit.id] = storehouseUnit.count;
        }

        return ingredients;
      },
      {},
    );

    commit('resetSelectedStorehouseUnits');

    let requestCallback = storehouseApi.createSharedTransaction;

    const payload: any = {
      products,
      ingredients,
      comment: transaction.comment,
      cost: transaction.cost,
    };

    if (TransactionType.REQUEST === transaction.type) {
      requestCallback = storehouseApi.createRequestTransaction;
    } else if (TransactionType.WRITE_OFF === transaction.type) {
      requestCallback = storehouseApi.createWriteOffTransaction;
    } else if (TransactionType.INSERTION === transaction.type) {
      requestCallback = storehouseApi.createInsertionTransaction;

      payload.contractor = transaction.contractor;
    }

    const transactionProps = await requestCallback(transaction.storehouse, payload);

    dispatch('socketCreateStorehouseTransaction', transactionProps, { root: true });
  },
  async editProduct({ dispatch }: ActionContext<StorehouseState, RootState>, payload: any) {
    const editedFields = await storehouseApi.editProduct(payload);

    return dispatch('socketEditProduct', editedFields, { root: true });
  },
  async editStorehouseProduct({ dispatch }: ActionContext<StorehouseState, RootState>, payload: any) {
    await storehouseApi.editProductRemainder(payload);

    return dispatch(
      'socketEditStorehouseProduct',
      {
        ...payload,
        maxRemainder: payload.maxCount,
      },
      { root: true },
    );
  },
  async removeProduct({ dispatch }: ActionContext<StorehouseState, RootState>, product: Product) {
    const ids = await storehouseApi.removeProduct(product.id);

    return dispatch('socketRemoveProduct', ids, { root: true });
  },
  async editIngredient({ dispatch }: ActionContext<StorehouseState, RootState>, payload: any) {
    const editedFields = await storehouseApi.editIngredient(payload);

    return dispatch('socketEditIngredient', editedFields, { root: true });
  },
  async editStorehouseIngredient({ dispatch }: ActionContext<StorehouseState, RootState>, payload: any) {
    await storehouseApi.editIngredientRemainder(payload);

    return dispatch(
      'socketEditStorehouseIngredient',
      {
        ...payload,
        maxRemainder: payload.maxCount,
      },
      { root: true },
    );
  },
  async removeIngredient({ dispatch }: ActionContext<StorehouseState, RootState>, ingredient: Ingredient) {
    const ids = await storehouseApi.removeIngredient(ingredient.id);

    return dispatch('socketRemoveIngredient', ids, { root: true });
  },
};

const mutations: MutationTree<StorehouseState> = {
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  selectStorehouseUnit(state, storehouseUnit) {
    state.selectedStorehouseUnits.push(storehouseUnit);
  },
  unselectStorehouseUnit(state, neededStorehouseUnit) {
    const storehouseUnitIndex = state.selectedStorehouseUnits.findIndex((storehouseUnit: StorehouseUnitInterface) => {
      return neededStorehouseUnit.id === storehouseUnit.id
        && (
          (neededStorehouseUnit instanceof StorehouseProduct && storehouseUnit instanceof StorehouseProduct)
          || (neededStorehouseUnit instanceof StorehouseIngredient && storehouseUnit instanceof StorehouseIngredient)
        );
    });

    if (-1 !== storehouseUnitIndex) {
      state.selectedStorehouseUnits.splice(storehouseUnitIndex, 1);
    }
  },
  resetSelectedStorehouseUnits(state) {
    state.selectedStorehouseUnits = [];
  },
  completeTransaction(state, payload: any) {
    const transaction: Transaction | undefined = state.transactions.find(
      (transaction: Transaction) => transaction.id === payload.id,
    );

    if (undefined !== transaction) {
      transaction.complete();

      for (const product of transaction.products) {
        const count = transaction.isReceive ? product.count : product.count * -1;
        const storehouseProduct = state.storehouseUnits.find(
          (storehouseUnit: StorehouseUnitInterface) => {
            return product.id === storehouseUnit.id && storehouseUnit instanceof StorehouseProduct;
          },
        );

        if (undefined !== storehouseProduct) {
          storehouseProduct.remainder += count;
        }
      }

      for (const ingredient of transaction.ingredients) {
        const count = transaction.isReceive ? ingredient.count : ingredient.count * -1;
        const storehouseIngredient = state.storehouseUnits.find(
          (storehouseUnit: StorehouseUnitInterface) => {
            return ingredient.id === storehouseUnit.id && storehouseUnit instanceof StorehouseIngredient;
          },
        );

        if (undefined !== storehouseIngredient) {
          storehouseIngredient.remainder += count;
        }
      }
    }
  },
  cancelTransaction(state, payload: any) {
    const transactionIndex: number = state.transactions.findIndex(
      (transaction: Transaction) => transaction.id === payload.id,
    );

    if (-1 !== transactionIndex) {
      state.transactions.splice(transactionIndex, 1);
    }
  },
  createTransaction(state, transactionProps: any) {
    let isReceive = true;

    if (
      null !== transactionProps.storehouseTransport
      && state.storehouses.some((storehouse: any) => storehouse.id === transactionProps.storehouseTransport.id)
    ) {
      [transactionProps.storehouseReceive, transactionProps.storehouseTransport] = [transactionProps.storehouseTransport, transactionProps.storehouseReceive];
      [transactionProps.staffReceive, transactionProps.staffTransport] = [transactionProps.staffTransport, transactionProps.staffReceive];

      isReceive = false;
    }

    const transaction = new Transaction(transactionProps, isReceive);

    state.transactions.unshift(transaction);

    if (transaction.isWriteOff || transaction.isInsertion) {
      for (const product of transaction.products) {
        const count = transaction.isReceive ? product.count : product.count * -1;
        const storehouseProduct = state.storehouseUnits.find(
          (storehouseUnit: StorehouseUnitInterface) => {
            return product.id === storehouseUnit.id && storehouseUnit instanceof StorehouseProduct;
          },
        );

        if (undefined !== storehouseProduct) {
          storehouseProduct.remainder += count;
        }
      }

      for (const ingredient of transaction.ingredients) {
        const count = transaction.isReceive ? ingredient.count : ingredient.count * -1;
        const storehouseIngredient = state.storehouseUnits.find(
          (storehouseUnit: StorehouseUnitInterface) => {
            return ingredient.id === storehouseUnit.id && storehouseUnit instanceof StorehouseIngredient;
          },
        );

        if (undefined !== storehouseIngredient) {
          storehouseIngredient.remainder += count;
        }
      }
    }
  },
  createProduct(state, productProps: ProductProps) {
    const product = StorehouseProduct.createFromRequest(productProps);

    if (!product.isHaveRequiredIngredients) {
      state.storehouseUnits.push(product);

      for (const storehouse of state.storehouses) {
        storehouse.products.push(product);
      }
    }
  },
  editProduct(state, productProps: any) {
    const requiredGroup = productProps.groups.find(({ type }: any) => type === 'required');

    if (requiredGroup.ingredients.length > 0) {
      state.storehouseUnits = removeById(state.storehouseUnits, productProps.oldProjectProductId);

      for (const storehouse of state.storehouses) {
        storehouse.products = removeById(storehouse.products, productProps.oldProjectProductId);
      }
    } else {
      const storehouseUnit: any = state.storehouseUnits.find((storehouseUnit: StorehouseUnitInterface) => {
        return storehouseUnit.id === productProps.oldProjectProductId;
      });

      if (undefined !== storehouseUnit) {
        storehouseUnit.mergeWithNewFields(productProps);
        storehouseUnit.id = productProps.projectProductId;
      } else {
        const product = StorehouseProduct.createFromRequest(productProps);
        product.id = productProps.projectProductId;

        state.storehouseUnits.push(product);

        for (const storehouse of state.storehouses) {
          storehouse.products.push(product);
        }
      }

      for (const storehouse of state.storehouses) {
        for (const product of storehouse.products) {
          if (product.id === productProps.oldProjectProductId) {
            product.mergeWithNewFields(productProps);
            product.id = productProps.projectProductId;
          }
        }
      }

      for (const storehouse of state.otherStorehouses) {
        for (const product of storehouse.products) {
          if (product.id === productProps.oldProjectProductId) {
            product.mergeWithNewFields(productProps);
            product.id = productProps.projectProductId;
          }
        }
      }

      for (const storehouse of state.otherStorehouses) {
        for (const product of storehouse.products) {
          if (product.id === productProps.oldProjectProductId) {
            product.mergeWithNewFields(productProps);
            product.id = productProps.projectProductId;
          }
        }
      }
    }
  },
  editStorehouseProduct(state, productProps: any) {
    const storehouseUnit: any = state.storehouseUnits.find((storehouseUnit: StorehouseUnitInterface) => {
      return storehouseUnit.id === productProps.id;
    });

    if (undefined !== storehouseUnit) {
      storehouseUnit.maxRemainder = productProps.maxCount;
    }

    for (const storehouse of state.storehouses) {
      for (const product of storehouse.products) {
        if (product.id === productProps.id) {
          product.maxRemainder = productProps.maxCount;
        }
      }
    }
  },
  removeProduct(state, id: string) {
    state.storehouseUnits = removeById(state.storehouseUnits, id);

    for (const storehouse of state.storehouses) {
      storehouse.products = removeById(storehouse.products, id);
    }
  },
  createIngredient(state, ingredientProps: IngredientProps) {
    const ingredient = StorehouseIngredient.createFromRequest(ingredientProps);

    state.storehouseUnits.push(ingredient);

    for (const storehouse of state.storehouses) {
      storehouse.ingredients.push(ingredient);
    }
  },
  editIngredient(state, ingredientProps: any) {
    const storehouseUnit: any = state.storehouseUnits.find((storehouseUnit: StorehouseUnitInterface) => {
      return storehouseUnit.id === ingredientProps.oldProjectIngredientId;
    });

    if (undefined !== storehouseUnit) {
      storehouseUnit.mergeWithNewFields(ingredientProps);
      storehouseUnit.id = ingredientProps.projectIngredientId;
    }

    for (const storehouse of state.storehouses) {
      for (const ingredient of storehouse.ingredients) {
        if (ingredient.id === ingredientProps.oldProjectIngredientId) {
          ingredient.mergeWithNewFields(ingredientProps);
          ingredient.id = ingredientProps.projectIngredientId;
        }
      }
    }

    for (const storehouse of state.otherStorehouses) {
      for (const ingredient of storehouse.ingredients) {
        if (ingredient.id === ingredientProps.oldProjectIngredientId) {
          ingredient.mergeWithNewFields(ingredientProps);
          ingredient.id = ingredientProps.projectIngredientId;
        }
      }
    }

    for (const storehouse of state.otherStorehouses) {
      for (const ingredient of storehouse.ingredients) {
        if (ingredient.id === ingredientProps.oldProjectIngredientId) {
          ingredient.mergeWithNewFields(ingredientProps);
          ingredient.id = ingredientProps.projectIngredientId;
        }
      }
    }
  },
  editStorehouseIngredient(state, ingredientProps: any) {
    const storehouseUnit: any = state.storehouseUnits.find((storehouseUnit: StorehouseUnitInterface) => {
      return storehouseUnit.id === ingredientProps.id;
    });

    if (undefined !== storehouseUnit) {
      storehouseUnit.maxRemainder = ingredientProps.maxCount;
    }

    for (const storehouse of state.storehouses) {
      for (const ingredient of storehouse.ingredients) {
        if (ingredient.id === ingredientProps.id) {
          ingredient.maxRemainder = ingredientProps.maxCount;
        }
      }
    }
  },
  removeIngredient(state, id: string) {
    state.storehouseUnits = removeById(state.storehouseUnits, id);

    for (const storehouse of state.storehouses) {
      storehouse.ingredients = removeById(storehouse.ingredients, id);
    }
  },
  writeOffProducts(state, products: any) {
    if (state.storehouses.length > 0) {
      for (const productId in products) {
        if (products.hasOwnProperty(productId)) {
          const storehouseProduct = state.storehouses[0].products.find(
            (product: StorehouseProduct): boolean => product.id === productId,
          );
          const storehouseUnit = state.storehouseUnits.find(
            (product: StorehouseUnitInterface): boolean => {
              return product.id === productId && product instanceof StorehouseProduct;
            },
          );

          if (undefined !== storehouseProduct) {
            storehouseProduct.remainder -= products[productId];
          }

          if (undefined !== storehouseUnit) {
            storehouseUnit.remainder -= products[productId];
          }
        }
      }
    }
  },
  writeOffIngredients(state, ingredients: any) {
    if (state.storehouses.length > 0) {
      for (const ingredientId in ingredients) {
        if (ingredients.hasOwnProperty(ingredientId)) {
          const storehouseIngredient = state.storehouses[0].ingredients.find(
            (ingredient: StorehouseProduct): boolean => ingredient.id === ingredientId,
          );
          const storehouseUnit = state.storehouseUnits.find(
            (product: StorehouseUnitInterface): boolean => {
              return product.id === ingredientId && product instanceof StorehouseIngredient;
            },
          );

          if (undefined !== storehouseIngredient) {
            storehouseIngredient.remainder -= ingredients[ingredientId];
          }

          if (undefined !== storehouseUnit) {
            storehouseUnit.remainder -= ingredients[ingredientId];
          }
        }
      }
    }
  },
  setStorehouseUnits(state, storehouseUnits: any) {
    state.storehouseUnits = storehouseUnits;
  },
  setStorehouses(state, storehouses: any) {
    state.storehouses = storehouses;
  },
  setOtherStorehouses(state, otherStorehouses: any) {
    state.otherStorehouses = otherStorehouses;
  },
  setTransactions(state, transactions) {
    state.transactions = transactions;
  },
  setStorehouseUnitSearchText(state, storehouseUnitSearchText: string) {
    state.storehouseUnitSearchText = storehouseUnitSearchText;
  },
  setGeneralSortType(state, generalSortType: string) {
    state.generalSortType = generalSortType;
  },
  setIsStorehouseFetching(state, isStorehouseFetching: boolean) {
    state.isStorehouseFetching = isStorehouseFetching;
  },
  setIsStorehouseWasFetch(state, isStorehouseWasFetch: boolean) {
    state.isStorehouseWasFetch = isStorehouseWasFetch;
  },
};

function removeById(list: any[], id: string) {
  const itemIndex = list.findIndex((item: any) => {
    return item.id === id;
  });

  if (-1 !== itemIndex) {
    list.splice(itemIndex, 1);
  }

  return list;
}

export default {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
